// src/pages/users/Users.jsx
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PersonOutline as PersonIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  LockPerson as LockIcon,
  LockOpen as UnlockIcon,
  Visibility as ViewIcon,
  Send as EmailIcon,
  LockOpenOutlined as LockOpen,
} from '@mui/icons-material';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';

// Import components
import EntityListContainer from '../../components/common/EntityListContainer';
import { EntityCard } from '../../design-system/components/data';
import StatusDisplay from '../../components/ui/StatusDisplay';
import ActionsMenu from '../../components/ui/ActionsMenu';
import SecureAvatar from '../../components/ui/SecureAvatar';
import ConfirmDialog from '../../components/ui/ConfirmDialog';

// Import stores
import useAuthStore from '../../stores/authStore';
import useUsersStore from '../../stores/userStore';
import useClientsStore from '../../stores/clientStore';
import useRoleStore from '../../stores/roleStore';

const Users = () => {
  const navigate = useNavigate();
  
  // Store hooks
  const { hasPermission } = useAuthStore();
  const { users, meta, isLoading, criticalError, fetchUsers, deleteUser, updateUserStatus } = useUsersStore();
  const { clients, fetchClients } = useClientsStore();
  const { roles, fetchRoles } = useRoleStore();
  
  // Local state for Confirm Dialogs and Action Menus
  const [actionMenus, setActionMenus] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: null, // 'delete' or 'status'
    userId: null,
    newStatus: null
  });
  const [rolesMap, setRolesMap] = useState({});
  
  // Check permissions
  const canCreate = hasPermission('users', 'create');
  const canUpdate = hasPermission('users', 'update');
  const canDelete = hasPermission('users', 'delete');
  
  // Fetch initial data
  useEffect(() => {
    if (clients.length === 0) {
      fetchClients();
    }
    
    if (roles.length === 0) {
      fetchRoles();
    }
  }, [fetchClients, fetchRoles, clients.length, roles.length]);
  
  // Process roles into a Map when they load
  useEffect(() => {
    if (roles.length > 0) {
      const map = {};
      
      roles.forEach(role => {
        // Determine color based on role type
        let color = 'default';
        if (role.key.includes('admin')) {
          color = role.key === 'superadmin' ? 'error' : 'warning';
        } else if (role.key.includes('manager')) {
          color = 'info';
        } else if (role.key.includes('client')) {
          color = 'primary';
        }
        
        map[role.key] = {
          label: role.name,
          color
        };
      });
      
      setRolesMap(map);
    }
  }, [roles]);
  
  // Handle open/close for action menus
  const handleOpenMenu = useCallback((e, userId) => {
    e.stopPropagation();
    setActionMenus(prev => ({
      ...prev,
      [userId]: e.currentTarget
    }));
  }, []);
  
  const handleCloseMenu = useCallback((userId) => {
    setActionMenus(prev => ({
      ...prev,
      [userId]: null
    }));
  }, []);
  
  // Handle confirmation dialogs
  const handleConfirmDelete = useCallback(async () => {
    if (confirmDialog.userId) {
      try {
        await deleteUser(confirmDialog.userId);
        // Close the dialog after successful deletion
        setConfirmDialog({ open: false, type: null, userId: null });
        
        // Optional: Refresh the users list to ensure consistency
        // This is not strictly necessary since the store updates the local state,
        // but it ensures the UI is in sync with the backend
        // await fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        // Keep the dialog open on error so user can see something went wrong
        // The error notification is already shown by the store
      }
    }
  }, [confirmDialog.userId, deleteUser]);
  
  const handleConfirmStatusChange = useCallback(async () => {
    if (confirmDialog.userId) {
      try {
        await updateUserStatus(confirmDialog.userId, confirmDialog.newStatus);
        setConfirmDialog({ open: false, type: null, userId: null, newStatus: null });
      } catch (error) {
        console.error('Error updating user status:', error);
      }
    }
  }, [confirmDialog.userId, confirmDialog.newStatus, updateUserStatus]);
  
  // Utility to get initials from name
  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };
  
  // Get client name by ID
  const getClientName = useCallback((clientId) => {
    if (!clientId) return 'N/A';
    const client = clients.find(c => c._id === clientId);
    return client ? client.name : 'Unknown Client';
  }, [clients]);
  
  // Entity renderer for grid view
  const renderUser = useCallback((user) => {
    // Safety check
    if (!user || !user._id) return null;
    
    // Get role info
    const roleInfo = rolesMap[user.role] || { label: user.role, color: 'default' };
    
    return (
      <>
        <EntityCard
          logo={
            <SecureAvatar 
              src={user.photo} 
              alt={user.fullName}
              version="thumbnail"
              sx={{ width: 64, height: 64 }}
            >
              {getInitials(user.firstName, user.lastName)}
            </SecureAvatar>
          }
          status={
            <StatusDisplay
              type="custom"
              customProps={{
                label: user.active ? 'Active' : 'Inactive',
                color: user.active ? 'success' : 'default',
                size: 'small'
              }}
            />
          }
          title={user.fullName}
          description={user.email}
          linkTo={`/users/${user._id}`}
          chips={[
            <StatusDisplay 
              key="role" 
              type="custom"
              customProps={{
                label: roleInfo.label,
                color: roleInfo.color,
                size: "small"
              }}
            />,
            user.clientId && (
              <StatusDisplay
                key="client"
                type="custom"
                customProps={{
                  label: getClientName(user.clientId),
                  color: "default",
                  size: "small"
                }}
              />
            )
          ]}
          menuButtonProps={{
            onClick: (e) => handleOpenMenu(e, user._id)
          }}
        />
        
        {/* Separate actions menu with proper anchoring */}
        <ActionsMenu
          anchorEl={actionMenus[user._id]}
          open={Boolean(actionMenus[user._id])}
          onClose={() => handleCloseMenu(user._id)}
          actions={[
            {
              label: 'View Details',
              onClick: () => navigate(`/users/${user._id}`),
            },
            ...(canUpdate ? [{
              label: 'Edit User',
              onClick: () => navigate(`/users/${user._id}/edit`),
            }] : []),
            ...(canUpdate ? [{
              label: user.active ? 'Deactivate' : 'Activate',
              onClick: () => setConfirmDialog({
                open: true,
                type: 'status',
                userId: user._id,
                newStatus: !user.active
              }),
            }] : [])
          ]}
          dangerActions={canDelete ? [
            {
              label: 'Delete User', 
              onClick: () => setConfirmDialog({
                open: true, 
                type: 'delete', 
                userId: user._id
              })
            }
          ] : []}
        />
      </>
    );
  }, [rolesMap, actionMenus, navigate, canUpdate, canDelete, handleOpenMenu, handleCloseMenu, getClientName]);
  
  // Table columns configuration
  const tableColumns = [
    { 
      id: 'name', 
      label: 'User',
      renderCell: (user) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SecureAvatar
            src={user.photo}
            alt={user.fullName}
            version="thumbnail"
            sx={{ width: 40, height: 40 }}
          >
            {getInitials(user.firstName, user.lastName)}
          </SecureAvatar>
          <Box>
            <Typography variant="body1" fontWeight={500}>
              {user.fullName}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {user.email}
            </Typography>
          </Box>
        </Box>
      )
    },
    { 
      id: 'role', 
      label: 'Role',
      align: 'center',
      renderCell: (user) => {
        const roleInfo = rolesMap[user.role] || { label: user.role, color: 'default' };
        return (
          <StatusDisplay
            type="custom"
            customProps={{
              label: roleInfo.label,
              color: roleInfo.color,
              size: "small"
            }}
          />
        );
      }
    },
    { 
      id: 'client', 
      label: 'Client',
      align: 'center',
      renderCell: (user) => (
        user.clientId ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2">
              {getClientName(user.clientId)}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="textSecondary">
            N/A
          </Typography>
        )
      )
    },
    { 
      id: 'status', 
      label: 'Status',
      align: 'center',
      renderCell: (user) => (
        <StatusDisplay
          type="custom"
          customProps={{
            label: user.active ? 'Active' : 'Inactive',
            color: user.active ? 'success' : 'default',
            size: "small"
          }}
        />
      )
    }
  ];
  
  // Table row actions renderer
  const getRowActions = useCallback((user) => {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Tooltip title="View Details">
          <IconButton 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/users/${user._id}`);
            }}
          >
            <ViewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        {canUpdate && (
          <Tooltip title="Edit User">
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/users/${user._id}/edit`);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        
        {canUpdate && (
          <Tooltip title={user.active ? "Deactivate User" : "Activate User"}>
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                setConfirmDialog({
                  open: true,
                  type: 'status',
                  userId: user._id,
                  newStatus: !user.active
                });
              }}
            >
              {user.active ? <LockIcon fontSize="small" /> : <LockOpen fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
        
        {canDelete && (
          <Tooltip title="Delete User">
            <IconButton 
              size="small" 
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                setConfirmDialog({
                  open: true, 
                  type: 'delete', 
                  userId: user._id
                });
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }, [canUpdate, canDelete, navigate]);
  
  // Filter configuration
  const filterConfig = [
    {
      name: 'role',
      label: 'Role',
      options: Object.entries(rolesMap).map(([value, { label }]) => ({ value, label }))
    },
    {
      name: 'clientId',
      label: 'Client',
      options: clients.map(client => ({ value: client._id, label: client.name }))
    },
    {
      name: 'status',
      label: 'Status',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' }
      ]
    }
  ];
  
  // Sort options
  const sortOptions = [
    { field: 'fullName', label: 'Name' },
    { field: 'email', label: 'Email' },
    { field: 'role', label: 'Role' },
    { field: 'createdAt', label: 'Created Date' },
    { field: 'updatedAt', label: 'Last Updated' }
  ];
  
  return (
    <>
      <EntityListContainer
        title="Users"
        description="Manage system users and their permissions"
        fetchData={fetchUsers}
        renderEntity={renderUser}
        initialFilters={{
          role: 'all',
          clientId: 'all',
          status: 'all'
        }}
        initialSort={{ field: 'createdAt', direction: 'desc' }}
        filterConfig={filterConfig}
        sortOptions={sortOptions}
        
        // View options
        availableViews={['grid', 'table']}
        defaultView="table"
        
        // Table configuration
        tableConfig={{
          columns: tableColumns,
          onRowClick: (user) => navigate(`/users/${user._id}`),
          renderRowActions: getRowActions
        }}
        
        // Use direct store values
        entities={users}
        isLoading={isLoading}
        error={null}  // Don't show error - let empty state handle it
        metadata={meta}
        
        actions={{
          canCreate,
          createPath: '/users/create',
          createLabel: 'Add User'
        }}
        emptyState={{
          title: 'No Users Found',
          description: 'Get started by adding your first user to the system.',
          icon: <PersonIcon sx={{ fontSize: 64 }} />,
          getFilteredTitle: () => 'No Matching Users',
          getFilteredDescription: () => 'Try adjusting your search or filters to find what you\'re looking for.'
        }}
      />
      
      {/* Confirmation Dialogs */}
      {confirmDialog.type === 'delete' && (
        <ConfirmDialog
          open={confirmDialog.open}
          title="Delete User"
          content="Are you sure you want to delete this user? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleConfirmDelete}
          onCancel={() => setConfirmDialog({ open: false, type: null, userId: null })}
          isDelete
          isLoading={isLoading}
        />
      )}
      
      {confirmDialog.type === 'status' && (
        <ConfirmDialog
          open={confirmDialog.open}
          title={confirmDialog.newStatus ? "Activate User" : "Deactivate User"}
          content={confirmDialog.newStatus 
            ? "Are you sure you want to activate this user? They will regain access to the system."
            : "Are you sure you want to deactivate this user? They will lose access to the system."
          }
          confirmText={confirmDialog.newStatus ? "Activate" : "Deactivate"}
          cancelText="Cancel"
          onConfirm={handleConfirmStatusChange}
          onCancel={() => setConfirmDialog({ open: false, type: null, userId: null, newStatus: null })}
          confirmButtonColor={confirmDialog.newStatus ? "success" : "error"}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export default Users;